import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
  { label: "Capabilities", href: "/#capabilities" },
  { label: "Media", href: "/#owned-media" },
  { label: "Framework", href: "/#framework" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Insights", href: "/insights" },
  { label: "Deck", href: "/deck" },
  { label: "Why RevGrow", href: "/#why" },
];

const ANNOUNCE_KEY = "revgrow.announce.dismiss.v1";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [announceOffset, setAnnounceOffset] = useState(false);

  useEffect(() => {
    try {
      setAnnounceOffset(localStorage.getItem(ANNOUNCE_KEY) !== "1");
    } catch { /* ignore */ }
    const onScroll = () => setScrolled(window.scrollY > 12);
    const onDismiss = () => setAnnounceOffset(false);
    window.addEventListener("scroll", onScroll);
    window.addEventListener("announcement:dismissed", onDismiss);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("announcement:dismissed", onDismiss);
    };
  }, []);

  return (
    <header
      data-testid="site-navbar"
      style={{ top: announceOffset ? "34px" : "0" }}
      className={`fixed inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className={`mx-auto max-w-[1440px] px-6 lg:px-10 transition-all ${scrolled ? "" : ""}`}>
        <div
          className={`flex items-center justify-between rounded-full border border-white/[0.06] px-5 py-2.5 transition-all ${
            scrolled ? "glass" : "bg-transparent"
          }`}
        >
          <a href="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full grid place-items-center bg-teal text-[#050A0F] font-display font-bold text-lg">
              R
            </div>
            <span className="font-display font-semibold text-[15px] tracking-tight">
              RevGrow<span className="text-teal">.</span>
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                href={l.href}
                className="text-[13px] text-muted-foreground hover:text-foreground transition-colors link-underline"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              data-testid="nav-cta"
              href="/#contact"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-teal text-[#050A0F] px-4 py-2 text-[13px] font-medium hover:opacity-90 transition"
            >
              Book a call <ArrowUpRight size={14} />
            </a>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden h-9 w-9 grid place-items-center rounded-full border border-white/10"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden mt-2 glass rounded-2xl p-4">
            <nav className="flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground py-1"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/#contact"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-teal text-[#050A0F] px-4 py-2 text-sm font-medium w-fit"
              >
                Book a call <ArrowUpRight size={14} />
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
